import * as React from 'react';
import '../../Css/Global/Global.css';
import { Grid } from "@mui/material";
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';


import DrfHierarchicalTable from 'src/Pages/AnalisesDrf/DrfHierarchicalTable';
import DrfPageFilter from 'src/Components/PageFilters/DrfPageFilter';
import { useDrfData } from 'src/Hooks/useDrfData';
import DrfFiltersStorageUtils, { DrfFiltersStorageData } from 'src/Storages/drfFiltersStorage';

const AnalisesDrf: React.FC = () => {
    // Hook para notificações globais
    const { enqueueSnackbar } = useSnackbar();
    
    // Função para obter filtros iniciais (salvos ou padrão)
    const getInitialFilters = (): DrfFiltersStorageData => {
        const savedFilters = DrfFiltersStorageUtils.get();
        if (savedFilters) {
            return savedFilters;
        }
        
        // Valores padrão se não houver filtros salvos
        return {
            dataInicio: dayjs().startOf('month').format('YYYY-MM-DD'),
            dataFim: dayjs().endOf('month').format('YYYY-MM-DD'),
            establishmentId: null
        };
    };
    
    const initialFilters = getInitialFilters();
    
    const [establishmentIdSelecionado, setEstablishmentIdSelecionado] = React.useState<number | string | null>(initialFilters.establishmentId);
    const [dataInicio, setDataInicio] = React.useState<string | undefined>(initialFilters.dataInicio);
    const [dataFim, setDataFim] = React.useState<string | undefined>(initialFilters.dataFim);
    
    // Estado para controlar quando deve fazer a requisição
    const [shouldFetch, setShouldFetch] = React.useState(false);
    
    const { 
        drfData, 
        loading, 
        error,
        manualFetch
    } = useDrfData(establishmentIdSelecionado, dataInicio, dataFim, shouldFetch);


    // Função para salvar filtros no localStorage
    const saveFilters = React.useCallback((dataInicio: string, dataFim: string, establishmentId: number | string | null) => {
        const filtersToSave: DrfFiltersStorageData = {
            dataInicio,
            dataFim,
            establishmentId
        };
        DrfFiltersStorageUtils.save(filtersToSave);
    }, []);
    
    // Todos os hooks devem estar antes dos returns condicionais
    const handleFiltersChange = React.useCallback((
        novaDataInicio?: string, 
        novaDataFim?: string, 
        establishmentId?: number | string | null
    ) => {
        if (novaDataInicio) setDataInicio(novaDataInicio);
        if (novaDataFim) setDataFim(novaDataFim);
        if (establishmentId !== undefined) {
            setEstablishmentIdSelecionado(establishmentId);
        }
        
        // Salva os filtros no localStorage
        if (novaDataInicio && novaDataFim) {
            saveFilters(novaDataInicio, novaDataFim, establishmentId || establishmentIdSelecionado);
        }

        // Dispara a busca imediatamente ao trocar filtros
        setShouldFetch(true);
        manualFetch();
    }, [saveFilters, establishmentIdSelecionado, manualFetch]);

    // Zera o gatilho após fetch manual, evitando chamadas em loop
    React.useEffect(() => {
        if (shouldFetch) {
            setShouldFetch(false);
        }
    }, [shouldFetch]);

    // Monitora erros e exibe notificação global
    React.useEffect(() => {
        if (error) {
            const is429Error = error.includes('Muitas requisições') || error.includes('429');
            
            const errorMessage = is429Error 
                ? 'Muitas requisições foram feitas em pouco tempo. Aguarde alguns segundos antes de tentar novamente.'
                : `Erro ao carregar dados do DRF: ${error}`;
                
            enqueueSnackbar(errorMessage, { variant: 'error' });
        }
    }, [error, enqueueSnackbar]);

    // Removido o display de erro inline - agora usa Snackbar

    return (
        <>
            <Grid container spacing={0.5}>
                {/* Filtros Integrados */}
                <Grid item xs={12}>
                    <DrfPageFilter
                        onFiltersChange={handleFiltersChange}
                        onManualFetch={manualFetch}
                        initialDataInicio={dataInicio}
                        initialDataFim={dataFim}
                        loading={loading}
                    />
                </Grid>
                
                {/* Tabela Hierárquica do DRF */}
                <Grid item xs={12}>
                    <DrfHierarchicalTable 
                        data={drfData} 
                        loading={loading}
                        title="Demonstrativo de Resultado Fiscal Detalhado"
                        establishmentId={establishmentIdSelecionado}
                        dataInicio={dataInicio}
                        dataFim={dataFim}
                        onTaxValuesUpdate={() => {
                            // Força a recarga dos dados usando manualFetch
                            manualFetch();
                        }}
                    />
                </Grid>
            </Grid>
        </>
    );
};

export default AnalisesDrf;
